import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {Price} from '@aboutyou/grpc-clients/tadarida/aysa_api/models//price.types';
import {BrandPreview} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/brand_preview.types';
import {PriceV2} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/price_v2.types';
import {GetProductBulkResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/article_detail_page/v1/service.types';
import {TrackingPromotionCreative} from '@aboutyou/legacy-tracking-sdk';
import {useHistory} from '@aboutyou/router';
import {Brand, TrackPromotions, useTrackOriginClick} from '@aboutyou/tracking';
import {
  BrandLogo,
  COLORS,
  ContentPopperProps,
  ErrorBoundary,
  FONT_SIZES,
  FONT_WEIGHTS,
  Heading14,
  ImageType,
  PriceLegalInfoPopup,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {memo, useEffect, useMemo} from 'react';
import {useActivatedCouponState} from '../../../features/Coupons/useActivatedCouponState';
import {SelectedSize} from '../../../features/SizeSelection/hooks/useSelectedSizeState';
import {useIsUrgencyTriggersDisabled} from '../../../shared-deprecated/useIsUrgencyTriggersDisabled';
import {PriceBox} from '../PriceAndBrandHorizontal/PriceBox';
import {CouponBadgeActive} from '../ProminentBannerSection/Coupons/CouponBadgeActive/CouponBadgeActive';
import {useIsQuickView} from '../QuickView/useIsQuickView';
import {PriceAndBrandReviewsSection} from '../ReviewsSection/PriceAndBrandReviewsSection/PriceAndBrandReviewsSection';
import {useBrandLocationWithState} from '../utils';
import {LogPriceDifference} from '../utils/LogPriceDifference';

const MemoPriceAndBrandReviewsSection = memo(PriceAndBrandReviewsSection);

export interface PriceAndBrandProps {
  className?: string;
  brand: {
    name: string;
    image?: ImageType;
    path: string;
    pathWithParameters: string;
  };
  brandPreview?: BrandPreview;
  brandPromotionTrackingData: Brand;
  selectedSize?: SelectedSize;
  condensedView?: boolean;
  productName?: string;
  productId: number;
  brandLogoAlignment?: string;
  shouldDisplayReviewsSection?: boolean;
  price?: Price | PriceV2;
  showActiveCouponBadge?: boolean;
  isInMiniADPDialog: boolean;
  isSoldOut?: boolean;
  setProductInfo?: (params: {
    productName: string;
    productBrandName: string;
  }) => void;
}

export const PriceAndBrand: React.FC<PriceAndBrandProps> = ({
  className,
  brand: {pathWithParameters, ...brand},
  brandPreview,
  brandPromotionTrackingData,
  selectedSize,
  condensedView = false,
  productName,
  productId,
  brandLogoAlignment,
  shouldDisplayReviewsSection,
  price,
  showActiveCouponBadge,
  isInMiniADPDialog,
  isSoldOut,
}) => {
  const trackOriginClick = useTrackOriginClick();
  const {couponPromotionId} = useActivatedCouponState(productId);
  const history = useHistory();
  const isQuickView = useIsQuickView();
  const {isActive: isPriceDisplayOverhaulActive} = useIsFeatureActive(
    'ab_price_display_overhaul_v2',
  );
  const disableUrgencyTriggers = useIsUrgencyTriggersDisabled();

  const brandPathWithState = useBrandLocationWithState({
    path: brand.path,

    brandPreview,
  });

  const onBrandLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    trackOriginClick('brands_logo')();

    history.push(pathWithParameters, brandPathWithState.state);
  };

  if (isSoldOut) {
    <BrandAndProductNameContainer data-quick-view={isQuickView}>
      <BrandLogo
        {...brand}
        path={brand.path}
        onClick={onBrandLogoClick}
        maxHeight="42px"
        maxWidth="200px"
        alignment={brandLogoAlignment}
      />
      <ProductName
        as="h1"
        data-testid="productName"
        data-quick-view={isQuickView}
      >
        {productName}
      </ProductName>
    </BrandAndProductNameContainer>;
  }

  return (
    <ErrorBoundary
      ErrorComponent={() => null}
      renderContext="product_price_and_brand"
    >
      <Container className={className}>
        <NoneKlarnaContentContainer
          data-condensed-view={condensedView}
          data-reduce-price-tags-spacing="true"
        >
          <StyledPriceBox
            productPrice={price}
            selectedSize={selectedSize}
            showCampaignCountdown={!disableUrgencyTriggers}
            showLargerFont
            key={productId}
            isInMiniADPDialog={isInMiniADPDialog}
            productId={productId}
          />
          {showActiveCouponBadge && !isPriceDisplayOverhaulActive && (
            <CouponBadgeActive
              productId={productId}
              key={couponPromotionId}
              isInMiniADPDialog={isInMiniADPDialog}
            />
          )}
          {shouldDisplayReviewsSection && <MemoPriceAndBrandReviewsSection />}
          <BrandAndProductNameContainer data-quick-view={isQuickView}>
            {!condensedView && productName && (
              <ProductName
                as="h1"
                data-testid="productName"
                data-quick-view={isQuickView}
              >
                {productName}
              </ProductName>
            )}
            <TrackPromotions
              promotion={{
                source: TrackingPromotionCreative.BrandReco,
                brand: brandPromotionTrackingData,
              }}
              streamPosition={NaN}
            >
              <BrandName
                data-testid="brandNameContainer"
                onClick={onBrandLogoClick}
              >
                {brand.name}
              </BrandName>
            </TrackPromotions>
          </BrandAndProductNameContainer>
        </NoneKlarnaContentContainer>
      </Container>
    </ErrorBoundary>
  );
};

export type PriceAndBrandViewProps = Omit<
  PriceAndBrandProps,
  | 'brand'
  | 'brandPreview'
  | 'brandPromotionTrackingData'
  | 'productName'
  | 'productId'
  | 'price'
  | 'isInMiniADPDialog'
  | 'openPriceLegalPopup'
> & {
  PriceLegalInfoLayoutComponent: ContentPopperProps['LayoutComponent'];
};

export const PriceAndBrandView: React.FC<
  GetProductBulkResponse['priceAndBrandSection'] &
    PriceAndBrandViewProps & {
      shouldShowBrandAsText?: boolean;
      shouldDisplayPriceAndBrandInRow?: boolean;
      isInMiniADPDialog: boolean;
    }
> = ({
  brandLogo,
  product,
  selectedSize,
  condensedView,
  className,
  brandLogoAlignment,
  shouldDisplayReviewsSection,
  price,
  priceV2,
  showActiveCouponBadge,
  isInMiniADPDialog,
  seoProductName,
  setProductInfo,
  sizePricesV2,
  PriceLegalInfoLayoutComponent,
}) => {
  const {isActive: isPriceDisplayOverhaulActive} = useIsFeatureActive(
    'ab_price_display_overhaul_v2',
  );
  useEffect(() => {
    // eslint-disable-next-line local-rules/set-state-in-use-effect
    setProductInfo?.({
      productBrandName: brandLogo.brand.name,
      productName: product.name,
    });
  }, []);

  const usedPrice = useMemo(() => {
    if (sizePricesV2 && selectedSize && isPriceDisplayOverhaulActive) {
      return sizePricesV2[selectedSize.sizeId];
    }

    return isPriceDisplayOverhaulActive ? priceV2 : price;
  }, [
    sizePricesV2,
    selectedSize,
    isPriceDisplayOverhaulActive,
    priceV2,
    price,
  ]);

  if (product?.isSoldOut) {
    return (
      <LogPriceDifference
        product={product}
        price={usedPrice}
        selectedSize={selectedSize}
        isInMiniADPDialog={isInMiniADPDialog}
      />
    );
  }

  return (
    <>
      <LogPriceDifference
        product={product}
        price={usedPrice}
        selectedSize={selectedSize}
        isInMiniADPDialog={isInMiniADPDialog}
      />
      <PriceAndBrand
        className={className}
        condensedView={condensedView}
        brand={{
          image: brandLogo.brand.blackLogoImage,
          name: brandLogo.brand.name,
          path: brandLogo.brand.path,
          pathWithParameters: brandLogo.path,
        }}
        brandPreview={brandLogo.brandPreview}
        brandPromotionTrackingData={{
          id: brandLogo.brand.id,
          name: brandLogo.brand.name,
          path: brandLogo.brand.path,
        }}
        selectedSize={selectedSize}
        productName={seoProductName ?? product.name}
        productId={product.id}
        brandLogoAlignment={brandLogoAlignment}
        shouldDisplayReviewsSection={shouldDisplayReviewsSection}
        price={usedPrice}
        showActiveCouponBadge={showActiveCouponBadge}
        isInMiniADPDialog={isInMiniADPDialog}
        isSoldOut={product.isSoldOut}
      />
      {PriceLegalInfoLayoutComponent && (
        <PriceLegalInfoPopup
          PriceLegalInfoLayoutComponent={PriceLegalInfoLayoutComponent}
        />
      )}
    </>
  );
};

const BrandName = styled.div`
  font-size: ${FONT_SIZES.px24};
  font-weight: ${FONT_WEIGHTS.heavy};
  line-height: 1.2;
  color: ${COLORS['Text.Black']};
  cursor: pointer;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const NoneKlarnaContentContainer = styled.div<{$reduceTopSpacing?: boolean}>`
  display: flex;
  flex-direction: column-reverse;
  align-items: flex-start;
  &[data-condensed-view='true'] {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0;

    padding-top: ${props => (props.$reduceTopSpacing ? '15px;' : '0')};
  }
`;

const BrandAndProductNameContainer = styled.div`
  display: flex;
  flex-direction: column-reverse;
  gap: 4px;

  &[data-quick-view='false'] {
    margin-top: 0.25rem;
  }
`;

const ProductName = styled(Heading14)`
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  margin: 0 0 0.25rem 0;

  &[data-quick-view='true'] {
    margin: 0 0 0.25rem 0;
  }
`;

const StyledPriceBox = styled(PriceBox)`
  width: 100%;
  padding-top: 1rem;
`;
